/**
 * Getting Started Checklist Module
 *
 * Shows new users a dismissible floating checklist of first actions after
 * creating their first budget. State persists in Firestore so it syncs
 * across devices.
 */

import { db } from './firebase.js';
import { doc, getDoc, setDoc } from 'firebase/firestore';

const STEPS = [
  {
    key: 'createBudget',
    label: 'Create a budget',
    description: 'Set up your income and expense categories',
    action: null, // auto-completed when wizard finishes
    optional: false,
  },
  {
    key: 'enterExpenseDetails',
    label: 'Enter expense details',
    description: 'Edit a line item name in your budget',
    action: { label: 'View Budget', fn: 'switchToSingleView', spotlight: 'enterExpenseDetails' },
    optional: false,
  },
  {
    key: 'logExpense',
    label: 'Log an expense',
    description: 'Record real spending against your budget',
    action: { label: 'Go to Expense Tracker', fn: 'switchToExpenseTracker', spotlight: 'logExpense' },
    optional: false,
  },
  {
    key: 'checkProgress',
    label: 'Check on your progress',
    description: 'See how your spending tracks against your plan',
    action: { label: 'View Progress', fn: 'switchToProgressView', spotlight: 'checkProgress' },
    optional: false,
  },
  {
    key: 'checkInsights',
    label: 'Check out your money insights',
    description: 'Visualize your cash flow with the Sankey diagram',
    action: { label: 'View Insights', fn: 'switchToSankeyView', spotlight: 'checkInsights' },
    optional: false,
  },
  {
    key: 'shareBudget',
    label: 'Share your budget',
    description: 'Invite a partner or family member to collaborate',
    action: { label: 'Share Budget', fn: 'showLinkCodeModal', spotlight: 'shareBudget' },
    optional: true,
  },
];

const STORAGE_KEY = 'budgley_onboarding';

let currentUserId = null;
let state = null;       // { dismissed, completedSteps: { ... } }
let cardOpen = true;    // in-memory: whether the float is currently visible
let pendingSpotlight = null; // step key waiting to pulse on next view load

// ─── State helpers ────────────────────────────────────────────────────────────

function defaultState() {
  return {
    dismissed: false,
    completedSteps: Object.fromEntries(STEPS.map(s => [s.key, false])),
  };
}

function allComplete() {
  // Complete when all non-optional steps are done
  return state && STEPS.filter(s => !s.optional).every(s => state.completedSteps[s.key]);
}

function completedCount() {
  if (!state) return 0;
  // Count only non-optional steps for the progress display
  return STEPS.filter(s => !s.optional && state.completedSteps[s.key]).length;
}

function totalRequired() {
  return STEPS.filter(s => !s.optional).length;
}

// ─── Persistence ──────────────────────────────────────────────────────────────

async function loadState(userId) {
  const cached = localStorage.getItem(`${STORAGE_KEY}_${userId}`);
  if (cached) {
    try {
      state = JSON.parse(cached);
      return;
    } catch (_) { /* fall through to Firestore */ }
  }

  try {
    const snap = await getDoc(doc(db, 'users', userId));
    if (snap.exists() && snap.data().onboarding) {
      state = snap.data().onboarding;
    } else {
      state = null; // no onboarding node → existing user, don't show
    }
    if (state) {
      localStorage.setItem(`${STORAGE_KEY}_${userId}`, JSON.stringify(state));
    }
  } catch (err) {
    console.error('gettingStarted: failed to load state', err);
    state = null;
  }
}

async function saveState() {
  if (!currentUserId || !state) return;
  localStorage.setItem(`${STORAGE_KEY}_${currentUserId}`, JSON.stringify(state));
  try {
    await setDoc(doc(db, 'users', currentUserId), { onboarding: state }, { merge: true });
  } catch (err) {
    console.error('gettingStarted: failed to save state', err);
  }
}

// ─── Public API ───────────────────────────────────────────────────────────────

/**
 * Initialize checklist for a newly authenticated user.
 * Pass `isNewUser = true` when the income wizard just completed.
 */
export async function initializeGettingStarted(userId, isNewUser = false) {
  currentUserId = userId;

  await loadState(userId);

  if (isNewUser && state === null) {
    state = defaultState();
    state.completedSteps.createBudget = true;
    await saveState();
  }

  render();
}

/**
 * Open (or re-open) the floating checklist. Called when header widget is clicked.
 */
export async function showGettingStarted() {
  if (!state) return;
  if (state.dismissed) {
    state.dismissed = false;
    await saveState();
  }
  cardOpen = true;
  render();
}

/**
 * Reset all steps except createBudget, un-dismiss, and reopen the card.
 * Called from Settings → App Behavior → Reset Onboarding.
 */
export async function resetGettingStarted() {
  if (!state) return;
  state.dismissed = false;
  STEPS.forEach(s => {
    state.completedSteps[s.key] = s.key === 'createBudget';
  });
  await saveState();
  cardOpen = true;
  render();
}

/**
 * Mark a checklist step as complete and re-render.
 * Safe to call even if checklist isn't active (no-ops gracefully).
 */
export async function markGettingStartedStep(stepKey) {
  if (!state) return;
  // Allow marking optional steps even after non-optional completion
  if (state.completedSteps[stepKey]) return;

  state.completedSteps[stepKey] = true;
  await saveState();

  if (allComplete()) {
    await autoComplete();
  } else {
    render();
  }
}

/**
 * Close the floating card (temporary — header widget remains to reopen it).
 */
export function closeGettingStarted() {
  cardOpen = false;
  removeCard();
}

/**
 * Permanently dismiss the checklist (removes header widget too).
 */
export async function dismissGettingStarted() {
  if (!state) return;
  state.dismissed = true;
  cardOpen = false;
  await saveState();
  removeCard();
}

// ─── Spotlight ────────────────────────────────────────────────────────────────

const SPOTLIGHT_SELECTORS = {
  enterExpenseDetails: () => document.querySelector('#expense-items label[contenteditable]'),
  logExpense:          () => document.getElementById('expense-category-select'),
  checkProgress:       () => document.getElementById('progress-view-mode'),
  checkInsights:       () => document.getElementById('sankey-container'),
  shareBudget:         () => document.getElementById('link-status-indicator'),
};

export function setPendingSpotlight(stepKey) {
  pendingSpotlight = stepKey;
}

export function applyPendingSpotlight() {
  if (!pendingSpotlight) return;
  const key = pendingSpotlight;
  pendingSpotlight = null;

  const getEl = SPOTLIGHT_SELECTORS[key];
  if (!getEl) return;

  setTimeout(() => {
    const el = getEl();
    if (!el) return;
    el.classList.add('gs-spotlight');
    setTimeout(() => el.classList.remove('gs-spotlight'), 2600);
  }, 200);
}

// ─── Rendering ────────────────────────────────────────────────────────────────

function renderHeaderWidget() {
  const widget = document.getElementById('onboarding-header-widget');
  if (!widget) return;

  if (!state || state.dismissed || allComplete()) {
    widget.style.display = 'none';
    return;
  }

  const count = completedCount();
  const total = totalRequired();
  const pct = Math.round((count / total) * 100);

  const fill = document.getElementById('onboarding-header-fill');
  const sub = document.getElementById('onboarding-header-sub');

  if (fill) fill.style.width = `${pct}%`;
  if (sub) sub.textContent = `${count} of ${total} tasks complete`;

  widget.style.display = 'flex';
}

function render() {
  renderHeaderWidget();

  if (!state || state.dismissed || !cardOpen) {
    removeCard();
    return;
  }

  const container = document.getElementById('getting-started-checklist');
  if (!container) return;

  const count = completedCount();
  const total = totalRequired();
  const pct = Math.round((count / total) * 100);

  const stepsHTML = STEPS.map(step => {
    const done = state.completedSteps[step.key];
    const optionalClass = step.optional ? 'gs-step--optional' : '';

    let actionHTML = '';
    if (!done && step.action) {
      const { label, fn, spotlight } = step.action;
      const spotlightCall = spotlight
        ? `window.setPendingSpotlight && window.setPendingSpotlight('${spotlight}'); `
        : '';
      actionHTML = `<button class="gs-action-link" onclick="${spotlightCall}window['${fn}'] && window['${fn}']()">${label} →</button>`;
    }

    return `
      <li class="gs-step ${done ? 'gs-step--done' : ''} ${optionalClass}">
        <span class="gs-step-icon" aria-hidden="true">
          ${done
            ? `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6 9 17l-5-5"/></svg>`
            : `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/></svg>`
          }
        </span>
        <span class="gs-step-body">
          <span class="gs-step-label">${step.label}</span>
          <span class="gs-step-desc">${step.description}</span>
        </span>
        ${actionHTML}
      </li>`;
  }).join('');

  container.innerHTML = `
    <div class="gs-card" id="gs-card">
      <div class="gs-header">
        <span class="gs-title">Getting Started</span>
        <div class="gs-header-actions">
          <button class="gs-dismiss" onclick="window.closeGettingStarted()" title="Close">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
          </button>
        </div>
      </div>
      <ul class="gs-steps">${stepsHTML}</ul>
      <div class="gs-footer">
        <span class="gs-progress-label">${count} of ${total} complete</span>
        <div class="gs-progress-bar" role="progressbar" aria-valuenow="${pct}" aria-valuemin="0" aria-valuemax="100">
          <div class="gs-progress-fill" style="width: ${pct}%"></div>
        </div>
      </div>
    </div>`;
}

async function autoComplete() {
  render();
  const card = document.getElementById('gs-card');
  if (card) card.classList.add('gs-card--completing');
  setTimeout(async () => {
    await dismissGettingStarted();
  }, 1800);
}

function removeCard() {
  const container = document.getElementById('getting-started-checklist');
  if (container) container.innerHTML = '';
  renderHeaderWidget();
}
